// Phantom Section Component - Enhanced with encrypted query parameter support for orgId and appId
// Now retrieves orgId and appId from URL query parameters (encrypted) with fallback to URL params
// No localStorage fallback - parameters must be provided via URL
import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useSearchParams, useParams, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Box,
  Alert,
  Chip,
  MenuItem,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Tabs,
  Tab,
  FormGroup,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import CalibrationlaboratoryService from '../../../services/calibrationlaboratoryService';
import { parseEncryptedQuery, decryptText, decryptAppId } from '../../../utils/encryption';

const PhantomSection = forwardRef(({ data = {}, onUpdate = () => {}, onComplete = () => {} }, ref) => {
  const [searchParams] = useSearchParams();
  const { orgId: urlOrgId } = useParams();
  const location = useLocation();
  
  // Function to get orgId from URL query parameters with decryption
  const getOrgIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log('📍 PhantomSection - Decrypted orgId from URL query:', decryptedOrgId);
        console.log('📍 PhantomSection - Original encrypted orgId:', encryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        console.log('⚠️ PhantomSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
        return encryptedOrgId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        console.log('📍 PhantomSection - Decrypted appId from URL query:', decryptedAppId);
        console.log('📍 PhantomSection - Original encrypted appId:', encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        console.log('⚠️ PhantomSection - Failed to decrypt appId, using original:', encryptedAppId, error);
        return encryptedAppId; // Return original if decryption fails
      }
    }
    return null;
  }, [location.search]);

  // Function to get orgId with priority: URL query (encrypted) > URL params > null
  const getOrgId = useCallback(() => {
    // First check URL query parameters (encrypted) - highest priority
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      console.log('📍 PhantomSection - Using decrypted orgId from URL query:', queryOrgId);
      return queryOrgId;
    }

    // Then check URL parameters (unencrypted)
    if (urlOrgId) {
      console.log('📍 PhantomSection - Using orgId from URL params:', urlOrgId);
      return urlOrgId;
    }

    // No fallback - orgId must be provided via URL
    console.log('⚠️ PhantomSection - No orgId found in URL parameters');
    return null;
  }, [getOrgIdFromQuery, urlOrgId]);

  // Function to get appId with priority: URL query (encrypted) only
  const getAppId = useCallback(() => {
    // Only check URL query parameters (encrypted)
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      console.log('📍 PhantomSection - Using decrypted appId from URL query:', queryAppId);
      return queryAppId;
    }

    // No fallback - appId must be provided via URL query parameters
    console.log('⚠️ PhantomSection - No appId found in URL query parameters');
    return null;
  }, [getAppIdFromQuery]);
  
  // Get form data from localStorage and URL parameters
  const getFormData = () => {
    // Parse encrypted query parameters
    const { disciplineId, disciplineName, tab } = parseEncryptedQuery(searchParams);
    
    console.log('🔓 Phantom Section - Decrypted Parameters:', {
      disciplineId,
      disciplineName,
      tab
    });

    return {
      appId: getAppId() || '',
      orgId: getOrgId() || '',
      disciplineId: disciplineId || '',
      disciplineName: disciplineName || '',
      userId: localStorage.getItem('userId') || '',
      initialTab: tab
    };
  };

  const formData = getFormData();
  const [phantoms, setPhantoms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingPhantom, setEditingPhantom] = useState(null);
  const [viewingPhantom, setViewingPhantom] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [disciplines, setDisciplines] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    // If tab parameter is provided from encrypted URL, use it directly
    if (formData.initialTab !== null && formData.initialTab !== undefined && formData.initialTab >= 0 && formData.initialTab <= 1) {
      console.log('🔓 Phantom Section - Setting active tab to:', formData.initialTab);
      return formData.initialTab;
    }
    return 0;
  });
  const [phantomTypes, setPhantomTypes] = useState([]);
  const [typeOfTestOptions, setTypeOfTestOptions] = useState([]);
  const [selectedTypeOfTests, setSelectedTypeOfTests] = useState([]);

  // Form state
  const [phantomData, setPhantomData] = useState({
    typeOfTest: [], // Changed to array for multiple selection
    phantomType: '',
    specifications: '',
    dateofPlacing: '',
    traceability: ''
  });

  // Load data on component mount
  useEffect(() => {
    // Load phantoms and disciplines when component mounts
    loadPhantoms();
    loadDisciplines();
    loadPhantomTypes();
    loadTypeOfTestOptions();
  }, []);

  // Listen for URL parameter changes to switch tabs
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'view' || tabParam === '1') {
      setActiveTab(1);
    } else if (tabParam === 'add' || tabParam === '0') {
      setActiveTab(0);
    }
  }, [searchParams]);

  // Load phantoms from API
  const loadPhantoms = async () => {
    try {
      setLoading(true);
      // Only load if we have the required data
      if (!formData.appId || !formData.orgId) {
        console.warn('Missing required data for loading phantoms');
        setPhantoms([]);
        return;
      }
      
      const response = await CalibrationlaboratoryService.getPhantoms(
        formData.appId,
        formData.orgId,
        formData.disciplineId
      );
      setPhantoms(response.data);
    } catch (error) {
      setError('Failed to load phantoms: ' + error.message);
      setPhantoms([]);
    } finally {
      setLoading(false);
    }
  };

  // Load disciplines for dropdown
  const loadDisciplines = async () => {
    try {
      const response = await CalibrationlaboratoryService.getDisciplines();
      setDisciplines(response.data || []);
    } catch (error) {
      console.error('Failed to load disciplines:', error);
    }
  };

  // Load phantom types from PhantomType table
  const loadPhantomTypes = async () => {
    try {
      const response = await CalibrationlaboratoryService.getPhantomTypes();
      setPhantomTypes(response.data || []);
    } catch (error) {
      console.error('Failed to load phantom types:', error);
      setPhantomTypes([]);
    }
  };

  // Load type of test options from form153Scope table
  const loadTypeOfTestOptions = async () => {
    try {
      
      // Debug localStorage values
      const { disciplineId: decryptedDisciplineId } = parseEncryptedQuery(searchParams);
      console.log('🔍 PhantomSection - Debug parameter values:', {
        appId: getAppId(),
        orgId: getOrgId(),
        disciplineId: decryptedDisciplineId,
        userId: localStorage.getItem('userId')
      });
      
      if (!formData.appId || !formData.orgId || !formData.disciplineId) {
        console.warn('Missing required data for loading type of test options:', {
          appId: formData.appId,
          orgId: formData.orgId,
          disciplineId: formData.disciplineId
        });
        setTypeOfTestOptions([]);
        return;
      }
      
      console.log('Calling API with:', {
        appId: formData.appId,
        orgId: formData.orgId,
        disciplineId: formData.disciplineId
      });
      
      const response = await CalibrationlaboratoryService.getTypeOfTestOptions(
        formData.appId,
        formData.orgId,
        formData.disciplineId
      );
      
      console.log('Type of test options response:', response);
      setTypeOfTestOptions(response.data || []);
    } catch (error) {
      console.error('Failed to load type of test options:', error);
      setTypeOfTestOptions([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setPhantomData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle checkbox changes for type of test
  const handleTypeOfTestChange = (testId, checked) => {
    setPhantomData(prev => ({
      ...prev,
      typeOfTest: checked 
        ? [...prev.typeOfTest, testId]
        : prev.typeOfTest.filter(id => id !== testId)
    }));
  };

  // Function to open View Existing tab (can be called from parent components)
  const openViewExistingTab = () => {
    setActiveTab(1);
  };

  // Function to open Add Phantom tab (can be called from parent components)
  const openAddPhantomTab = () => {
    setActiveTab(0);
  };

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    openViewExistingTab,
    openAddPhantomTab,
    setActiveTab
  }));

  // Open add tab
  const handleAddPhantom = () => {
    setEditingPhantom(null);
    setPhantomData({
      typeOfTest: [],
      phantomType: '',
      specifications: '',
      dateofPlacing: '',
      traceability: ''
    });
    setActiveTab(0); // Switch to Add Phantom tab
  };

  // Open edit tab
  const handleEditPhantom = (phantom) => {
    setEditingPhantom(phantom);
    setPhantomData({
      typeOfTest: phantom.TypeOfTest ? phantom.TypeOfTest.split(',').map(id => parseInt(id)) : [],
      phantomType: phantom.PhantomType || '',
      specifications: phantom.Specifications || '',
      dateofPlacing: phantom.DateofPlacing ? phantom.DateofPlacing.split('T')[0] : '',
      traceability: phantom.Traceability || ''
    });
    setActiveTab(0); // Switch to Add Phantom tab for editing
  };

  // Open view dialog
  const handleViewPhantom = (phantom) => {
    setViewingPhantom(phantom);
    setViewDialogOpen(true);
  };

  // Save phantom (add or update)
  const handleSavePhantom = async () => {
    try {
      setError('');
      setLoading(true);

      // Validate required data
      if (!formData.appId || !formData.orgId) {
        setError('Missing required application or organization data');
        return;
      }

      const payload = {
        ...phantomData,
        typeOfTest: phantomData.typeOfTest.join(','), // Convert array to comma-separated string
        appId: formData.appId,
        orgId: formData.orgId,
        disciplineId: formData.disciplineId, // Get from localStorage/URL query
        labLocationId: '', // Default empty value
        groupId: '', // Default empty value
        dateofPlacing: phantomData.dateofPlacing ? phantomData.dateofPlacing : null,
        updatedById: formData.userId || null,
        clientIp: null
      };

      if (editingPhantom) {
        await CalibrationlaboratoryService.updatePhantom(editingPhantom.Id, payload);
        setSuccess('Phantom updated successfully!');
      } else {
        await CalibrationlaboratoryService.addPhantom(payload);
        setSuccess('Phantom added successfully!');
      }

      // Clear form and reset editing state
      setEditingPhantom(null);
      setPhantomData({
        typeOfTest: [],
        phantomType: '',
        specifications: '',
        dateofPlacing: '',
        traceability: ''
      });
      
      // Switch to view existing tab to see the new/updated phantom
      setActiveTab(1);
      await loadPhantoms();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to save phantom: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete phantom
  const handleDeletePhantom = async (phantom) => {
    if (window.confirm(`Are you sure you want to delete the phantom "${phantom.PhantomType}"?`)) {
      try {
        setLoading(true);
        await CalibrationlaboratoryService.deletePhantom(
          phantom.Id,
          formData.userId || null,
          null
        );
        setSuccess('Phantom deleted successfully!');
        await loadPhantoms();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Failed to delete phantom: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Close dialogs
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPhantom(null);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingPhantom(null);
  };

  // Get discipline name by ID
  const getDisciplineName = (disciplineId) => {
    console.log('Getting discipline name for ID:', disciplineId, 'Type:', typeof disciplineId);
    console.log('Available disciplines:', disciplines);
    
    if (!disciplineId || disciplines.length === 0) return 'N/A';
    
    // Handle both string and number comparisons safely
    const discipline = disciplines.find(d => {
      if (!d || d.id === undefined || d.id === null) return false;
      
      return (
        d.id === disciplineId ||
        d.id === parseInt(disciplineId) ||
        (d.id !== null && d.id !== undefined ? d.id.toString() : '') === (disciplineId !== null && disciplineId !== undefined ? disciplineId.toString() : '')
      );
    });
    
    console.log('Found discipline:', discipline);
    return discipline ? discipline.disciplineName : `Unknown (ID: ${disciplineId})`;
  };

  // Format type of test display
  const formatTypeOfTest = (typeOfTestString) => {
    console.log('Formatting type of test:', typeOfTestString);
    console.log('Available type of test options:', typeOfTestOptions);
    
    if (!typeOfTestString) return 'N/A';
    
    const testIds = typeOfTestString.split(',').map(id => parseInt(id)).filter(id => !isNaN(id));
    console.log('Parsed test IDs:', testIds);
    
    const testNames = testIds.map(id => {
      const option = typeOfTestOptions.find(opt => {
        // Handle both string and number IDs with proper conversion
        return (opt && (
          opt.id === id || 
          opt.Id === id || 
          parseInt(opt.id) === id || 
          parseInt(opt.Id) === id ||
          opt.id === id.toString() ||
          opt.Id === id.toString()
        ));
      });
      console.log(`Looking for ID ${id}, found:`, option);
      return option ? option.TypeOfTest : `Unknown (ID: ${id})`;
    });
    
    console.log('Final test names:', testNames);
    return testNames.join(', ');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
            List of Phantoms
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(event, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem',
                  minWidth: 120,
                  padding: '12px 24px'
                }
              }}
            >
              <Tab 
                label="Add Phantom" 
                icon={<AddIcon />} 
                iconPosition="start"
                sx={{ color: '#2e7d32' }}
              />
              <Tab 
                label="View Existing" 
                icon={<ViewIcon />} 
                iconPosition="start"
                sx={{ color: '#1976d2' }}
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <CardContent sx={{ p: 0 }}>
            {/* Add Phantom Tab */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#2e7d32' }}>
                  {editingPhantom ? 'Edit Phantom' : 'Add New Phantom'}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ mb: 2, color: '#333', fontWeight: 500 }}>
                        Type of Test *
                      </FormLabel>
                      <FormGroup>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {typeOfTestOptions.map((option) => (
                            <FormControlLabel
                              key={option.id || option.Id}
                              control={
                                <Checkbox
                                  checked={phantomData.typeOfTest.includes(parseInt(option.id || option.Id))}
                                  onChange={(e) => handleTypeOfTestChange(parseInt(option.id || option.Id), e.target.checked)}
                                  size="small"
                                />
                              }
                              label={option.TypeOfTest}
                              sx={{ 
                                minWidth: '200px',
                                '& .MuiFormControlLabel-label': {
                                  fontSize: '0.875rem'
                                }
                              }}
                            />
                          ))}
                        </Box>
                        {typeOfTestOptions.length === 0 && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            No type of test options available for this discipline.
                          </Typography>
                        )}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Phantom Type"
                      value={phantomData.phantomType}
                      onChange={(e) => handleInputChange('phantomType', e.target.value)}
                      size="small"
                      required
                    >
                      {phantomTypes.map((type) => (
                        <MenuItem key={type.Id} value={type.Name}>
                          {type.Name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date of Placing"
                      type="date"
                      value={phantomData.dateofPlacing || ''}
                      onChange={(e) => handleInputChange('dateofPlacing', e.target.value)}
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Specifications"
                      value={phantomData.specifications}
                      onChange={(e) => handleInputChange('specifications', e.target.value)}
                      multiline
                      rows={3}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Traceability"
                      value={phantomData.traceability}
                      onChange={(e) => handleInputChange('traceability', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setPhantomData({
                            typeOfTest: [],
                            phantomType: '',
                            specifications: '',
                            dateofPlacing: '',
                            traceability: ''
                          });
                        }}
                      >
                        Clear Form
                      </Button>
                      <Button 
                        variant="contained" 
                        startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSavePhantom}
                        disabled={loading || !phantomData.phantomType}
                        sx={{
                          backgroundColor: '#2e7d32',
                          '&:hover': { backgroundColor: '#1b5e20' }
                        }}
                      >
                        {loading ? 'Saving...' : (editingPhantom ? 'Update Phantom' : 'Save Phantom')}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* View Existing Tab */}
            {activeTab === 1 && (
              <Box>
                {loading && !dialogOpen ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>S.No.</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Phantom Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type of Test</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Specifications</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date of Placing</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Traceability</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {phantoms.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body1" color="textSecondary">
                            No phantoms found. Click "Add Phantom" to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      phantoms.map((phantom, index) => (
                        <TableRow key={phantom.Id} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 120 }}>
                              {phantom.PhantomType}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getDisciplineName(phantom.DisciplineId)} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{                             
                            }}>
                              {formatTypeOfTest(phantom.TypeOfTest)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              maxWidth: 200, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {phantom.Specifications || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {phantom.DateofPlacing ? 
                              new Date(phantom.DateofPlacing).toLocaleDateString() : 'N/A'
                            }
                          </TableCell>
                          <TableCell>{phantom.Traceability || 'N/A'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="Edit">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEditPhantom(phantom)}
                                  sx={{ color: '#ed6c02' }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDeletePhantom(phantom)}
                                  sx={{ color: '#d32f2f' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: '#f5f5f5', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Typography variant="h6">
              {editingPhantom ? 'Edit Phantom' : 'Add New Phantom'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lab Location ID"
                  value={phantomData.labLocationId}
                  onChange={(e) => handleInputChange('labLocationId', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Discipline *"
                  value={phantomData.disciplineId}
                  onChange={(e) => handleInputChange('disciplineId', e.target.value)}
                  size="small"
                  required
                >
                  {disciplines.map((discipline) => (
                    <MenuItem key={discipline.Id} value={discipline.Id}>
                      {discipline.Name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Type of Test"
                  value={phantomData.typeOfTest}
                  onChange={(e) => handleInputChange('typeOfTest', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Group ID"
                  value={phantomData.groupId}
                  onChange={(e) => handleInputChange('groupId', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phantom Type *"
                  value={phantomData.phantomType}
                  onChange={(e) => handleInputChange('phantomType', e.target.value)}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Placing"
                  type="date"
                  value={phantomData.dateofPlacing || ''}
                  onChange={(e) => handleInputChange('dateofPlacing', e.target.value)}
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Specifications"
                  value={phantomData.specifications}
                  onChange={(e) => handleInputChange('specifications', e.target.value)}
                  multiline
                  rows={3}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Traceability"
                  value={phantomData.traceability}
                  onChange={(e) => handleInputChange('traceability', e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
            <Button onClick={handleCloseDialog} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleSavePhantom} 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
              disabled={loading || !phantomData.phantomType || !phantomData.disciplineId}
            >
              {loading ? 'Saving...' : (editingPhantom ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={handleCloseViewDialog} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: '#f5f5f5', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Typography variant="h6">Phantom Details</Typography>
            <IconButton onClick={handleCloseViewDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {viewingPhantom && (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Phantom Type</Typography>
                  <Typography variant="body1">{viewingPhantom.PhantomType}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Discipline</Typography>
                  <Typography variant="body1">{getDisciplineName(viewingPhantom.DisciplineId)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Type of Test</Typography>
                  <Typography variant="body1">{formatTypeOfTest(viewingPhantom.TypeOfTest)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Group ID</Typography>
                  <Typography variant="body1">{viewingPhantom.GroupId || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Lab Location ID</Typography>
                  <Typography variant="body1">{viewingPhantom.LabLocationId || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Date of Placing</Typography>
                  <Typography variant="body1">
                    {viewingPhantom.DateofPlacing ? 
                      new Date(viewingPhantom.DateofPlacing).toLocaleDateString() : 'N/A'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Specifications</Typography>
                  <Typography variant="body1">{viewingPhantom.Specifications || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Traceability</Typography>
                  <Typography variant="body1">{viewingPhantom.Traceability || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created Date</Typography>
                  <Typography variant="body1">
                    {viewingPhantom.InsertDate ? 
                      new Date(viewingPhantom.InsertDate).toLocaleString() : 'N/A'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Last Updated</Typography>
                  <Typography variant="body1">
                    {viewingPhantom.UpdateDate ? 
                      new Date(viewingPhantom.UpdateDate).toLocaleString() : 'N/A'
                    }
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
            <Button onClick={handleCloseViewDialog} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
});

export default PhantomSection;